/*
This software is Copyright ©️ 2020 The University of Southern California. All Rights Reserved. 
Permission to use, copy, modify, and distribute this software and its documentation for educational, research and non-profit purposes, without fee, and without a written agreement is hereby granted, provided that the above copyright notice and subject to the full license file found in the root of this software deliverable. Permission to make commercial use of this software may be obtained by contacting:  USC Stevens Center for Innovation University of Southern California 1150 S. Olive Street, Suite 2300, Los Angeles, CA 90115, USA Email: accounting@stevens.usc.edu

The full terms of this copyright and license should always be found in the root directory of this software deliverable as "license.txt" and if these terms are not found with this software, please contact the USC Stevens Center for the full license.
*/
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import {
  fetchOrgs,
  updateOrganization,
  deleteOrganization as _deleteOrganization,
} from "../../../api";
import { RootState } from "../../store";
import { Edits, Organization, LoadStatus } from "../../../types";

export interface OrganizationsState {
  organizations: Record<string, Organization>;
  organizationEdits: Record<string, Edits<Organization>>;
  organizationLoadStatus: LoadStatus;
}

const initialState: OrganizationsState = {
  organizations: {},
  organizationEdits: {},
  organizationLoadStatus: LoadStatus.NONE,
};

/** Actions */

export const loadOrganizations = createAsyncThunk(
  "organizations/loadOrganizations",
  async (_, thunkAPI): Promise<Organization[]> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await fetchOrgs(state.login.accessToken);
  }
);

export const addOrUpdateOrganization = createAsyncThunk(
  "organizations/addOrUpdateOrganization",
  async (data: Partial<Organization>, thunkAPI): Promise<Organization> => {
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    return await updateOrganization(state.login.accessToken, data);
  }
);

export const deleteOrganization = createAsyncThunk(
  "organizations/deleteOrganization",
  async (id: string, thunkAPI): Promise<void> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken) {
      throw new Error("login required");
    }
    await _deleteOrganization(state.login.accessToken, id);
  }
);

export const revertOrganization = createAsyncThunk(
  "organizations/revertOrganization",
  async (id: string, thunkAPI): Promise<Organization> => {
    if (!id) throw new Error("id required");
    const state = thunkAPI.getState() as RootState;
    if (!state.login.user || !state.login.accessToken)
      throw new Error("login required");
    const edits = state.organizations.organizationEdits[id];
    if (
      !edits ||
      !edits.oldData ||
      `${JSON.stringify(edits.oldData)}` === `${JSON.stringify(edits.newData)}`
    ) {
      throw new Error("no changes to revert");
    }
    return await updateOrganization(state.login.accessToken, edits.oldData);
  }
);

export const organizationsSlice = createSlice({
  name: "organizations",
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // load organizations
      .addCase(loadOrganizations.pending, (state) => {
        state.organizationLoadStatus = LoadStatus.IN_PROGRESS;
      })
      .addCase(loadOrganizations.fulfilled, (state, action) => {
        for (const organization of action.payload) {
          state.organizations[organization.id] = organization;
          state.organizationEdits[organization.id] = {
            oldData: organization,
          };
        }
        state.organizationLoadStatus = LoadStatus.DONE;
      })
      .addCase(loadOrganizations.rejected, (state) => {
        state.organizationLoadStatus = LoadStatus.FAILED;
      })
      // add or update organization
      .addCase(addOrUpdateOrganization.fulfilled, (state, action) => {
        const organization = action.payload;
        if (state.organizations[organization.id]) {
          state.organizationEdits[organization.id].newData = organization;
        } else {
          state.organizationEdits[organization.id] = {
            oldData: organization,
            created: true,
          };
        }
        state.organizations[organization.id] = organization;
      })
      // delete organization
      .addCase(deleteOrganization.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.organizations[id];
      })
      // revert organization
      .addCase(revertOrganization.fulfilled, (state, action) => {
        const id = action.meta.arg;
        state.organizations[id] = action.payload;
        state.organizationEdits[id].oldData = action.payload;
        state.organizationEdits[id].newData = undefined;
      });
  },
});

export default organizationsSlice.reducer;
